import type { EventInstance } from '../types';
import { getEventColor, formatTime } from '../utils';

interface MonthCellProps {
  date: Date;
  isCurrentMonth: boolean;
  isToday: boolean;
  events: EventInstance[];
  maxVisible?: number;
  showEventTimes?: boolean;
  onClickDate: (date: Date) => void;
  onClickEvent: (event: EventInstance) => void;
  dimPastEvents?: boolean;
  eventColorPriority?: 'event' | 'calendar';
}

export function MonthCell({
  date,
  isCurrentMonth,
  isToday,
  events,
  maxVisible = 3,
  showEventTimes = false,
  onClickDate,
  onClickEvent,
  dimPastEvents = false,
  eventColorPriority = 'event',
}: MonthCellProps) {
  const visibleEvents = events.slice(0, maxVisible);
  const overflow = events.length - maxVisible;

  return (
    <div
      className={`
        min-h-[100px] border-b border-r p-1 cursor-pointer
        ${!isCurrentMonth ? 'bg-muted/30' : ''}
        hover:bg-muted/50 transition-colors
      `}
      onClick={() => onClickDate(date)}
    >
      {/* Day number */}
      <div className="flex items-center justify-center mb-0.5">
        <span
          className={`
            text-xs font-medium w-6 h-6 flex items-center justify-center rounded-full
            ${isToday ? 'bg-primary text-primary-foreground' : ''}
            ${!isCurrentMonth ? 'text-muted-foreground' : ''}
          `}
        >
          {date.getDate()}
        </span>
      </div>

      {/* Event chips */}
      <div className="space-y-0.5">
        {visibleEvents.map((event) => {
          const metadata = (event.metadata || {}) as Record<string, unknown>;
          const isFocusTime = metadata.focusTime === true;
          const color = getEventColor(event, eventColorPriority);
          const isPast = dimPastEvents && new Date(event.endAt) < new Date();
          const timePrefix =
            showEventTimes && !event.isAllDay ? formatTime(new Date(event.startAt)) + ' ' : '';

          return (
            <button
              key={event.id}
              type="button"
              className={`w-full text-[10px] leading-tight truncate rounded px-1 py-0.5 cursor-pointer hover:opacity-80 text-left ${isPast ? 'opacity-50' : ''}`}
              style={{
                backgroundColor: isFocusTime ? 'transparent' : color + '20',
                color,
                borderLeft: `2px ${isFocusTime ? 'dashed' : 'solid'} ${color}`,
                ...(isFocusTime
                  ? {
                      backgroundImage: `repeating-linear-gradient(135deg, ${color}10, ${color}10 2px, transparent 2px, transparent 4px)`,
                    }
                  : {}),
              }}
              onClick={(e) => {
                e.stopPropagation();
                onClickEvent(event);
              }}
              title={event.title}
            >
              {timePrefix}
              {event.title}
            </button>
          );
        })}

        {overflow > 0 && (
          <div className="text-[10px] text-muted-foreground font-medium px-1">+{overflow} more</div>
        )}
      </div>
    </div>
  );
}
